import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { maintenanceAPI } from "../../utils/api";
import Layout from "../common/Layout";
import MaintenanceRequestForm from "./MaintenanceRequestForm";
import MyMaintenanceRequests from "./MyMaintenanceRequests";

const ScholarDashboard = () => {
  const { userProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch maintenance request stats
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await maintenanceAPI.getStats();
      setStats(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching stats:", error);
      setError("Failed to load maintenance statistics");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Scholar Dashboard">
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              Scholar Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Welcome back, {userProfile?.name}!
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton
              onClick={logout}
              color="error"
              sx={{
                border: 1,
                borderColor: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "white",
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Error Display */}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssignmentIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total</Typography>
                </Box>
                <Typography variant="h3" color="info.main">
                  {loading ? "..." : stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">Pending</Typography>
                </Box>
                <Typography variant="h3" color="warning.main">
                  {loading ? "..." : stats.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BuildIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">In Progress</Typography>
                </Box>
                <Typography variant="h3" color="info.main">
                  {loading ? "..." : stats.inProgress}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <HistoryIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Resolved</Typography>
                </Box>
                <Typography variant="h3" color="success.main">
                  {loading ? "..." : stats.resolved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Chip label="Closed" color="default" sx={{ mr: 1 }} />
                  <Typography variant="h6">Closed</Typography>
                </Box>
                <Typography variant="h3" color="text.secondary">
                  {loading ? "..." : stats.closed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Paper sx={{ p: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab
              label="Create Request"
              icon={<AddIcon />}
              iconPosition="start"
            />
            <Tab
              label="My Requests"
              icon={<AssignmentIcon />}
              iconPosition="start"
            />
          </Tabs>

          {activeTab === 0 && <MaintenanceRequestForm onSuccess={fetchStats} />}
          {activeTab === 1 && <MyMaintenanceRequests />}
        </Paper>
      </Box>
    </Layout>
  );
};

export default ScholarDashboard;
